/**
 * §9 — THE capability table. Every `FormatCapabilities` row in the product lives here.
 *
 * A row is FORMAT KNOWLEDGE, so it belongs to whoever owns the writer (contract 1.2.0, and
 * CTO S4/Q1). CORE owns the `project()` machinery that consumes them and the registry that
 * holds them; the values are ours.
 *
 * TWO MECHANICAL RULES, from contract 1.2.0:
 *   • A row travels WITH its writer through `registerWriter(def)`, which refuses a row whose
 *     `format` disagrees. A writer without its row is unrepresentable.
 *   • The five P1/P2 members have no writer yet and still need rows, registered on their own
 *     (eight until contract 1.13.0 gave TTML, SCC and EBU STL theirs),
 *     through `registerCapabilities(row)`. `capabilities(target)` THROWS on a missing row, and
 *     the download row renders from the API's format list rather than a hardcoded template —
 *     so a P2 format reaching a user is a configuration change, not a code change, and it will
 *     happen before anyone remembers this file.
 *
 * `cueSettings` and `formatColumns` are stated per format rather than derived. CORE was
 * deriving them as `sizePct && positioning !== 'none'` and `namedStyles && actor && effect`,
 * which are statements about SHAPE standing in for statements about CAPABILITY. Both
 * derivations are correct for the launch set. `sbv` is the first row that breaks the second
 * one (no named styles, no actor, no effect, and no column passthrough either — same answer,
 * different reason), and `ttml` broke the first while its row described the whole format rather
 * than the writer 1.13.0 added (regions and cue settings without `size:`).
 *
 * WHERE THESE DISAGREE WITH `test/core/_fixtures/caps.ts`, THESE ARE RIGHT — that file says so
 * itself. The two real disagreements are recorded at the rows that carry them (`sub.maxLines`
 * and `ass.annotations`).
 */

import type { FormatCapabilities, WriteFormat } from '../../contracts/cue-model.ts';

/** ms, cs and ds as tick quanta. Exact, by the 120 000 lattice. */
const MS = 120;
const CS = 1200;

/**
 * The all-off row. Every real row states its differences from this, so a reader can see what
 * a format CAN do without reading twenty-five fields of `false`.
 */
const NOTHING: Omit<FormatCapabilities, 'format'> = {
  namedStyles: false,
  layers: false,
  positioning: 'none',
  regions: false,
  vertical: false,
  sizePct: false,
  karaoke: false,
  perWordTiming: false,
  inline: [],
  colorEncoding: 'none',
  multiLine: true,
  maxLines: null,
  allowsOverlap: true,
  multiTrack: false,
  comments: false,
  drawings: false,
  annotations: false,
  timeQuantum: MS,
  frameBased: false,
  requiresFramerate: false,
  actor: false,
  effect: false,
  binary: false,
  cueSettings: false,
  formatColumns: false,
};

/*───────────────────────────────────────────────────────────────────────────────────────
  The twelve launch write formats
───────────────────────────────────────────────────────────────────────────────────────*/

/**
 * SRT. No formal spec; behaviour is decided by ffmpeg's `srtenc.c`/`subviewer` family, VLC
 * and mpv. All three render `<i> <b> <u>` and `<font color>`; none of them carries a named
 * style table, a layer, or a region.
 *
 * `positioning: 'none'` is the honest default and it has a known consequence: `project()`
 * drops position and emits 'position-dropped' BEFORE the writer runs, so `WriteOptions
 * .anchorAsSrtTag` can never see a position on the projected document. That is a contract
 * gap, not a row error — `ProjectionOptions` carries `formatOptions` (which is how PDF's
 * `styled:false` degrades correctly) but not `anchorAsSrtTag`. Filed to the CTO. The writer
 * implements the tag anyway, because a writer is TOTAL over whatever document it is handed.
 */
export const SRT: FormatCapabilities = {
  ...NOTHING,
  format: 'srt',
  inline: ['bold', 'italic', 'underline', 'color'],
  colorEncoding: 'font-tag',
};

/**
 * WebVTT (W3C). Cue settings, regions, vertical writing and `size:` are all in the spec;
 * inline `<00:00:01.000>` timestamps are the per-word carrier (§6.3 "WebVTT cue timestamp"),
 * and `<ruby>`/`<rt>` is the only annotation home in the launch set.
 *
 * `annotations: true` means what CTO S10 ruled it means: **this format can carry an `'above'`
 * reading, and nothing else is carried through projection.** WebVTT ruby is above-only, and mode
 * `'replace'` is materialised into `run.text` by the transform rather than surviving as an
 * annotation. `write/vtt.ts` emits it; a row claiming this capability without emitting a reading
 * into its bytes fails `test/convert/loss.test.ts`, which is the guard that made this row honest.
 *
 * `comments: false` deliberately. A WebVTT `NOTE` block is not a timed cue — it carries no
 * times at all — so a commented cue cannot survive as one. It is dropped with a loss entry
 * rather than being silently reshaped into something that renders differently.
 */
export const VTT: FormatCapabilities = {
  ...NOTHING,
  format: 'vtt',
  positioning: 'alignment',
  regions: true,
  vertical: true,
  sizePct: true,
  perWordTiming: true,
  inline: ['bold', 'italic', 'underline', 'color', 'background', 'classes'],
  colorEncoding: 'css',
  annotations: true,
  cueSettings: true,
};

/**
 * ASS (Advanced SubStation Alpha v4.00+), as Aegisub writes it and libass renders it.
 *
 * `annotations: false` — and this is the one place `test/core/_fixtures/caps.ts` is wrong.
 * ASS has no ruby/annotation markup of any kind. A pinyin annotation in mode 'above' has
 * nowhere to go in ASS, and saying it does would drop it with NO loss entry, which is the
 * single failure the whole capability design exists to prevent. Modes 'replace' and
 * 'extra-line' materialise into text and lines and are unaffected.
 */
export const ASS: FormatCapabilities = {
  ...NOTHING,
  format: 'ass',
  namedStyles: true,
  layers: true,
  positioning: 'coordinates',
  karaoke: true,
  perWordTiming: true,
  inline: [
    'bold',
    'italic',
    'underline',
    'strikeout',
    'color',
    'fontName',
    'fontSize',
    'outline',
    'shadow',
    'scale',
    'rotation',
    'blur',
  ],
  colorEncoding: 'ass-bgr',
  comments: true,
  drawings: true,
  timeQuantum: CS,
  actor: true,
  effect: true,
  formatColumns: true,
};

/**
 * SSA v4.00 — ASS's predecessor, and NOT merely "ASS with a different header".
 *
 * The [V4 Styles] Format line has no ScaleX, ScaleY, Spacing, Angle, StrikeOut or
 * BorderStyle-3 opaque box, it carries TertiaryColour where ASS carries OutlineColour, and
 * its alignment is the 1/2/3 + 4(top) + 8(mid) scheme rather than the numpad. Everything in
 * that list is a capability SSA does not have, so the row differs from ASS in exactly those
 * places and the difference is reported as loss rather than quietly written into a field the
 * renderer ignores.
 */
export const SSA: FormatCapabilities = {
  ...NOTHING,
  format: 'ssa',
  namedStyles: true,
  layers: false, // SSA has no Layer column; the [Events] Format line begins with Marked=
  positioning: 'coordinates',
  karaoke: true,
  perWordTiming: true,
  inline: ['bold', 'italic', 'underline', 'color', 'fontName', 'fontSize', 'outline', 'shadow'],
  colorEncoding: 'ass-bgr',
  comments: true,
  drawings: true,
  timeQuantum: CS,
  actor: true,
  effect: true,
  formatColumns: true,
};

/**
 * SAMI. `multiTrack` is the whole point of the format: `<P Class=ENUSCC>` and
 * `<P Class=KRCC>` interleave two language streams in ONE file, and a model without a track
 * axis reads a bilingual SAMI as one interleaved cue list with one language destroyed.
 *
 * Styling is CSS in the `<STYLE>` block plus inline `<b>/<i>/<u>/<font>`, so classes,
 * colours, families and sizes all survive. Positioning does not: SAMI positions through CSS
 * properties this model does not carry.
 */
export const SMI: FormatCapabilities = {
  ...NOTHING,
  format: 'smi',
  inline: ['bold', 'italic', 'underline', 'color', 'background', 'classes', 'fontName', 'fontSize'],
  colorEncoding: 'css',
  multiTrack: true,
};

/**
 * MicroDVD. `requiresFramerate` is this format's entire trap expressed as one flag: a frame
 * index means nothing without an exact rate, and the competitor's silent 23.976 is wrong for
 * every PAL source.
 *
 * `maxLines: null`, and this is the second place `test/core/_fixtures/caps.ts` differs. There
 * is no line limit in MicroDVD: `|` is a separator and every renderer that reads the format
 * (VLC, MPC-HC, mpv via lavf) displays as many segments as it is given. Claiming 2 would make
 * `project()` merge a legitimate three-line cue and report 'line-count-clamped' for a limit
 * that does not exist — inventing loss is the same defect as hiding it.
 *
 * `allowsOverlap: false` is real: the format has no layer and no z-order, and consecutive
 * frame ranges that overlap render as one replacing the other.
 */
export const SUB: FormatCapabilities = {
  ...NOTHING,
  format: 'sub',
  // {Y:i} italic/bold/underline, {C:$bbggrr}, {F:name}, {S:size} — the documented control set.
  inline: ['bold', 'italic', 'underline', 'color', 'fontName', 'fontSize'],
  // `{C:$BBGGRR}` is byte-reversed hex, the same byte order as ASS's &HBBGGRR without alpha.
  colorEncoding: 'ass-bgr',
  allowsOverlap: false,
  timeQuantum: 'frame',
  frameBased: true,
  requiresFramerate: true,
  // `{P:1}`, `{H:lang}`, `{o:…}`, `{D:…}` — control codes this model does not type, carried in
  // `Cue.extra` and re-emitted verbatim. That is exactly what `formatColumns` describes, and
  // saying `false` here would make `project()` drop them with a loss entry on a SUB → SUB
  // conversion that loses nothing at all.
  formatColumns: true,
};

/**
 * TXT — not a format, a projection. It carries text and nothing else, which is exactly why
 * its row is almost entirely `false`: every conversion to it should report what it destroyed.
 *
 * `timeQuantum: 1` (one tick) rather than a millisecond quantum, because TXT emits no
 * timestamps at all in its default shape; quantizing to a grid nothing is written on would
 * manufacture a 'precision-reduced' entry for a precision loss that did not happen.
 */
export const TXT: FormatCapabilities = { ...NOTHING, format: 'txt', timeQuantum: 1 };

/**
 * PDF. Binary output, a real column layout, and the only launch format with an `actor`
 * column that is not an ASS descendant.
 *
 * `PdfFormatOptions.styled === false` degrades this row inside `project()` (it is threaded
 * through `ProjectionOptions.formatOptions`), which is the mechanism that makes an
 * option-dependent capability report loss instead of losing it in the writer.
 */
export const PDF: FormatCapabilities = {
  ...NOTHING,
  format: 'pdf',
  inline: ['bold', 'italic', 'underline', 'color'],
  colorEncoding: 'css',
  actor: true,
  binary: true,
};

/**
 * LRC, base dialect. `multiLine: false` / `maxLines: 1` is the format's defining constraint:
 * one `[mm:ss.xx]` stamp introduces exactly one line of lyric, so a two-line cue must be
 * merged and REPORTED rather than emitted as a second stamped line at the same time.
 */
export const LRC: FormatCapabilities = {
  ...NOTHING,
  format: 'lrc',
  multiLine: false,
  maxLines: 1,
  timeQuantum: CS,
};

/**
 * Enhanced LRC (A2). Identical to LRC except that `<mm:ss.xx>` word stamps make per-word
 * timing carriable — which is why they are two `WriteFormat` members sharing one `.lrc`
 * extension (adapter §10) rather than one format with a flag: `project()` has to degrade
 * differently for them.
 */
export const LRC_ENHANCED: FormatCapabilities = {
  ...NOTHING,
  format: 'lrc-enhanced',
  perWordTiming: true,
  multiLine: false,
  maxLines: 1,
  timeQuantum: CS,
};

/**
 * SBV — YouTube's caption-editor export. No cue numbers, no styling, no positioning.
 *
 * `allowsOverlap: false`: YouTube's caption model is a strictly sequential list, and its own
 * editor refuses overlapping cues on import. Emitting overlap into SBV produces a file the
 * target tool rejects, so the overlap is collapsed and reported here instead.
 */
export const SBV: FormatCapabilities = {
  ...NOTHING,
  format: 'sbv',
  allowsOverlap: false,
};

/**
 * JSON — the cue model's own serialization, and therefore the ONE lossless target.
 *
 * Every field is true on purpose. This row is load-bearing in a way the others are not: it
 * is what makes "export to JSON, edit elsewhere, re-import" a promise rather than a hope, and
 * a single `false` here would be a silent, permanent data loss on the round trip that exists
 * to have none. `timeQuantum: 1` because ticks are written raw.
 */
export const JSON_CAPS: FormatCapabilities = {
  format: 'json',
  namedStyles: true,
  layers: true,
  positioning: 'coordinates',
  regions: true,
  vertical: true,
  sizePct: true,
  karaoke: true,
  perWordTiming: true,
  inline: [
    'bold',
    'italic',
    'underline',
    'strikeout',
    'color',
    'background',
    'fontName',
    'fontSize',
    'outline',
    'shadow',
    'scale',
    'rotation',
    'blur',
    'classes',
  ],
  colorEncoding: 'css',
  multiLine: true,
  maxLines: null,
  allowsOverlap: true,
  multiTrack: true,
  comments: true,
  drawings: true,
  annotations: true,
  timeQuantum: 1,
  frameBased: false,
  requiresFramerate: false,
  actor: true,
  effect: true,
  binary: false,
  cueSettings: true,
  formatColumns: true,
};

/**
 * DOCX (contract 1.12.0) — a Word document, for people who edit, comment on or review the text
 * rather than play it. Binary, like PDF: a ZIP of WordprocessingML (`write/docx.ts`).
 *
 * `inline` is what a Word run property carries one-to-one and every reader (Word, Google Docs,
 * LibreOffice) renders: bold, italic, underline, strike-out.
 *
 * `colorEncoding: 'none'`, DELIBERATELY. Word could hold a colour, and subtitle colours are
 * chosen for a dark video: white and yellow, which on a white page are unreadable. A review copy
 * the reviewer cannot read is worse than one that says the colour was left out, so `project()`
 * reports 'color-dropped' instead.
 *
 * `actor: false`, `positioning: 'none'`, no named styles, no comments: the document is a table of
 * start, end and text, or plain paragraphs (`DocxFormatOptions.layout`), with nowhere else to put
 * any of those. Each is dropped and reported by `project()` before the writer runs.
 *
 * `timeQuantum` stays a millisecond: the table writes `00:01:02,345`, SRT's own form. The
 * 'paragraphs' layout writes no times, and that is the user's choice rather than a capability, so
 * it is not this row's to describe (contract 1.12.0).
 */
export const DOCX: FormatCapabilities = {
  ...NOTHING,
  format: 'docx',
  inline: ['bold', 'italic', 'underline', 'strikeout'],
  binary: true,
};

/*───────────────────────────────────────────────────────────────────────────────────────
  The P1/P2 members — rows without writers (TTML, SCC and EBU STL have writers since 1.13.0)

  These are NOT placeholders. `capabilities(target)` throws on a missing row, the download
  row renders from the API's format list rather than a hardcoded template, and a P2 format
  reaching a user is therefore a configuration change rather than a code change. When a writer
  for one of these is built it registers through `registerWriter`, which will refuse a row
  that disagrees with the one already here — so getting the values right now is what makes
  that refusal useful later.
───────────────────────────────────────────────────────────────────────────────────────*/

/**
 * TTML / DFXP (contract 1.13.0) — the row of the writer that exists (`write/ttml.ts`), which is
 * smaller than the format. TTML can hold regions, fonts, ruby and named styles; this writer puts
 * every cue in one bottom region and styles text inline, so a row claiming more would make
 * `project()` keep things the writer then silently leaves out, the one failure this table exists
 * to prevent. What it does write is what SRT holds, with CSS colours, so SRT to TTML loses
 * nothing and the TTML reader (`parse/ttml.ts`) reads every one of these back.
 */
export const TTML: FormatCapabilities = {
  ...NOTHING,
  format: 'ttml',
  inline: ['bold', 'italic', 'underline', 'color'],
  colorEncoding: 'css',
};

/** PGS/SUP — a stream of BITMAPS. It carries pixels, so it carries every visual property and
 *  no text property at all. Binary. Nothing in the launch set can produce one. */
export const SUP: FormatCapabilities = {
  ...NOTHING,
  format: 'sup',
  positioning: 'coordinates',
  inline: ['bold', 'italic', 'underline', 'color', 'fontName', 'fontSize', 'outline', 'shadow'],
  colorEncoding: 'palette',
  binary: true,
};

/** VobSub — an .idx index plus a .sub bitmap stream, four-colour palette per subtitle. */
export const SUBIDX: FormatCapabilities = {
  ...NOTHING,
  format: 'subidx',
  positioning: 'coordinates',
  inline: ['color'],
  colorEncoding: 'palette',
  multiTrack: true,
  binary: true,
};

/** SubViewer 2.0 — `[INFORMATION]` header plus `hh:mm:ss.cc,hh:mm:ss.cc` pairs, `[br]` line
 *  separator, no styling. Centisecond precision, hence the quantum. */
export const SUBVIEWER: FormatCapabilities = {
  ...NOTHING,
  format: 'subviewer',
  timeQuantum: CS,
};

/**
 * SCC (contract 1.13.0) — CEA-608 captions as hex words, written by `write/scc.ts`. Frame-based:
 * the writer refuses without a rate, and the pages send 29.97 (the rate SCC is defined at).
 * Italics, underline and the seven-colour palette; position as alignment (bottom, middle or top
 * rows; left, centre or right).
 *
 * `maxLines: null`, although a 608 caption holds four rows of 32 columns: the limit is two-
 * dimensional and the writer meets it itself, breaking long lines and showing a cue of more than
 * four rows as consecutive captions, and says so (`line-rebroken`, `timing-interpolated`).
 * Saying 4 here would make `project()` merge a fifth line into the fourth before the writer could
 * do better with it.
 */
export const SCC: FormatCapabilities = {
  ...NOTHING,
  format: 'scc',
  positioning: 'alignment',
  inline: ['italic', 'underline', 'color'],
  colorEncoding: 'palette',
  allowsOverlap: false,
  timeQuantum: 'frame',
  frameBased: true,
  requiresFramerate: true,
};

/**
 * EBU STL (EBU Tech 3264, contract 1.13.0) — binary, written by `write/ebu-stl.ts` as open
 * subtitles at 25 or 30 fps. The open-subtitle text field has italics, underline and boxing
 * codes; the writer keeps italics, which is what the page promises ("styling beyond italics
 * doesn't"), so underline, bold and colour are dropped by `project()` and reported. Position is
 * the vertical position and justification fields. Rows are limited to 40 characters by the
 * writer, which breaks longer ones and says so, the same way SCC's 32 are handled.
 */
export const EBU_STL: FormatCapabilities = {
  ...NOTHING,
  format: 'ebu-stl',
  positioning: 'alignment',
  inline: ['italic'],
  allowsOverlap: false,
  timeQuantum: 'frame',
  frameBased: true,
  requiresFramerate: true,
  binary: true,
};

/** CSV — a spreadsheet projection. Rows carry index, times, actor and text; nothing else. */
export const CSV: FormatCapabilities = {
  ...NOTHING,
  format: 'csv',
  actor: true,
  formatColumns: true, // extra columns are literally extra columns
};

/** XLSX — CSV plus cell formatting, and binary. */
export const XLSX: FormatCapabilities = {
  ...NOTHING,
  format: 'xlsx',
  inline: ['bold', 'italic', 'underline', 'color'],
  colorEncoding: 'hex',
  actor: true,
  binary: true,
  formatColumns: true,
};

/*───────────────────────────────────────────────────────────────────────────────────────
  Lookup
───────────────────────────────────────────────────────────────────────────────────────*/

/** Rows whose writer exists. Registered THROUGH the writer, never on their own. */
export const WRITER_ROWS: Readonly<Record<string, FormatCapabilities>> = {
  srt: SRT,
  vtt: VTT,
  ass: ASS,
  ssa: SSA,
  smi: SMI,
  sub: SUB,
  txt: TXT,
  pdf: PDF,
  lrc: LRC,
  'lrc-enhanced': LRC_ENHANCED,
  sbv: SBV,
  json: JSON_CAPS,
  docx: DOCX,
  // Contract 1.13.0: the TV and streaming formats.
  ttml: TTML,
  scc: SCC,
  'ebu-stl': EBU_STL,
};

/** The five members with no writer. Registered on their own; not optional. */
export const STUB_ROWS: readonly FormatCapabilities[] = [SUP, SUBIDX, SUBVIEWER, CSV, XLSX];

/** Every row, for tests and for the completeness assertion. */
export const ALL_ROWS: readonly FormatCapabilities[] = [
  SRT,
  VTT,
  ASS,
  SSA,
  SMI,
  SUB,
  TXT,
  PDF,
  LRC,
  LRC_ENHANCED,
  SBV,
  JSON_CAPS,
  DOCX,
  TTML,
  SCC,
  EBU_STL,
  ...STUB_ROWS,
];

/** The row for a format, from this module rather than the registry — used by writers that
 *  need their own row before registration has happened (tests, and `registerWriter` itself). */
export function rowFor(format: WriteFormat): FormatCapabilities {
  const row = ALL_ROWS.find((r) => r.format === format);
  if (row === undefined) {
    throw new RangeError(`capabilities: no row authored for '${format}'`);
  }
  return row;
}
